import { Key, Check, AlertCircle } from 'react-feather';
import { useState, useMemo } from 'react';
import { API_BASE_URL } from '../../config/api';

/* Change password, on its own card, separate from two-factor, which is a
   one-line on/off decision rather than a form.

   Strength is shown as a meter, not a checklist. Nielsen Norman's password
   research (Egelman et al. 2013) found strength meters motivate genuinely
   stronger passwords, while a list of unmet rules only signals compliance:
   users satisfy the hoops and stop. The meter is paired with ONE adaptive
   hint line so the rules are still discoverable, a bar with no guidance
   swaps a nagging checklist for a silent one. */

const MIN_LENGTH = 8;

/* Score 0-4, from the properties that actually resist guessing: length first
   (the single strongest factor), then character variety. Deliberately not a
   rule tally, "meets 3 of 4 rules" is the checklist we are replacing. */
const scorePassword = (value) => {
  // 0 is reserved for "nothing typed yet", which renders no bar and no text.
  if (!value) return 0;

  // Anything under the floor is Weak outright. Returning a computed score
  // here would fall through to 0 for a short simple password, the meter
  // would go silent at exactly the point the user needs telling it is too
  // short, which is the state this replaced a checklist to avoid.
  if (value.length < MIN_LENGTH) return 1;

  let score = 1;
  if (value.length >= 12) score += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
  if (/[0-9]/.test(value)) score += 1;
  if (/[^a-zA-Z0-9]/.test(value)) score += 1;

  score = Math.min(score, 4);

  // Variety alone cannot reach the top rung: a short password with every
  // character class would otherwise read "Strong" while the hint below it
  // still asks for more length, which contradicts itself.
  if (value.length < 12) score = Math.min(score, 3);

  return score;
};

const LEVELS = [
  { key: 'empty', label: '' },
  { key: 'weak', label: 'Weak' },
  { key: 'fair', label: 'Fair' },
  { key: 'good', label: 'Good' },
  { key: 'strong', label: 'Strong' }
];

/* One hint at a time, in the order that most improves the score. Replaces the
   four-line "need uppercase / need number / …" list: the user is told the
   single next thing worth doing, not everything they have not done. */
const hintFor = (value) => {
  // Silent until the user types. An empty field has nothing to critique, and
  // a standing instruction there is the checklist behaviour in one line.
  if (!value) return '';
  if (value.length < MIN_LENGTH) {
    const remaining = MIN_LENGTH - value.length;
    return `Add ${remaining} more character${remaining === 1 ? '' : 's'}.`;
  }
  if (value.length < 12) return 'Longer passwords are harder to guess.';
  if (!(/[a-z]/.test(value) && /[A-Z]/.test(value))) return 'Mix upper and lower case to strengthen it.';
  if (!/[0-9]/.test(value)) return 'Add a number to strengthen it.';
  if (!/[^a-zA-Z0-9]/.test(value)) return 'Add a symbol to strengthen it.';
  return 'This is a strong password.';
};

const EMPTY_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const PasswordSection = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [revealed, setRevealed] = useState({});

  const score = useMemo(() => scorePassword(formData.newPassword), [formData.newPassword]);
  // Once the bar is full the hint confirms rather than asks. Without this a
  // password can read "Strong" while still being told to add a symbol, which
  // reads as the meter contradicting itself.
  const hint = useMemo(
    () => (score === 4 ? 'This is a strong password.' : hintFor(formData.newPassword)),
    [formData.newPassword, score]
  );
  const level = LEVELS[score];

  // The floor for submission stays the same as before, 8 characters with a
  // letter and a number. The meter reports quality above that; it does not
  // relax what the backend will accept.
  const meetsFloor =
    formData.newPassword.length >= MIN_LENGTH &&
    /[a-zA-Z]/.test(formData.newPassword) &&
    /[0-9]/.test(formData.newPassword);

  const matches =
    formData.newPassword.length > 0 &&
    formData.newPassword === formData.confirmPassword;

  const canSubmit =
    formData.currentPassword.length > 0 && meetsFloor && matches && !loading;

  // Shown under the confirm field only once the user has typed there, so the
  // mismatch warning does not appear while they are still mid-entry.
  const showMismatch =
    formData.confirmPassword.length > 0 &&
    formData.newPassword !== formData.confirmPassword;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setMessage({ type: '', text: '' });
  };

  const toggleReveal = (field) =>
    setRevealed((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Your password has been changed.' });
        setFormData(EMPTY_FORM);
        setRevealed({});

        // Preserved from the original: the backend rotates the Activepieces
        // token on password change, so the stale one must be cleared.
        localStorage.removeItem('ap_token');
        localStorage.removeItem('projectId');
        if (data.ap_token) {
          localStorage.setItem('ap_token', data.ap_token);
          localStorage.setItem('projectId', data.ap_project_id);
        }
      } else {
        setMessage({
          type: 'error',
          text: data.detail || 'Failed to change password. Please try again.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'An error occurred while changing your password. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordField = ({ name, label, autoComplete, describedBy }) => (
    <div className={`account-field${name === 'confirmPassword' && showMismatch ? ' has-error' : ''}`}>
      <label htmlFor={`password-${name}`}>{label}</label>
      <div className="account-input-affix">
        <input
          id={`password-${name}`}
          type={revealed[name] ? 'text' : 'password'}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          autoComplete={autoComplete}
          aria-describedby={describedBy}
          aria-invalid={name === 'confirmPassword' && showMismatch ? 'true' : undefined}
          required
        />
        <button
          type="button"
          className="account-reveal-button"
          onClick={() => toggleReveal(name)}
          // The label states the action, not the state, and names the field
          // so it is unambiguous when three of these are on one form.
          aria-label={`${revealed[name] ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
        >
          {revealed[name] ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );

  return (
    <section className="account-section-card account-card-grow">
      <div className="account-section-header">
        <span className="account-section-icon" aria-hidden="true">
          <Key />
        </span>
        <div className="account-section-heading">
          <h2 className="account-section-title">Change password</h2>
          <p className="account-section-description">
            Choose a strong password you don't use anywhere else.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="account-section-body">
          {message.text && (
            <div className={`account-banner ${message.type}`} role="alert">
              {message.type === 'success' ? (
                <Check aria-hidden="true" />
              ) : (
                <AlertCircle aria-hidden="true" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          <div className="account-field-group">
            {passwordField({
              name: 'currentPassword',
              label: 'Current password',
              autoComplete: 'current-password'
            })}

            {passwordField({
              name: 'newPassword',
              label: 'New password',
              autoComplete: 'new-password',
              describedBy: 'password-strength'
            })}

            {/* The meter replaces the requirements checklist. Four segments
                rather than one continuous bar: discrete steps make progress
                legible without implying false precision about entropy. */}
            <div
              className={`account-strength is-${level.key}`}
              id="password-strength"
            >
              <div
                className="account-strength-track"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={4}
                aria-valuenow={score}
                aria-valuetext={
                  formData.newPassword ? `Password strength: ${level.label}` : 'No password entered'
                }
              >
                {[1, 2, 3, 4].map((step) => (
                  <span
                    key={step}
                    className={`account-strength-segment${score >= step ? ' filled' : ''}`}
                  />
                ))}
              </div>
              {/* aria-live so the hint is announced as it changes, but only
                  the hint, announcing the bar too would double up. */}
              <p className="account-strength-meta" aria-live="polite">
                <span className="account-strength-label">{level.label}</span>
                <span className="account-strength-hint">{hint}</span>
              </p>
            </div>

            <div>
              {passwordField({
                name: 'confirmPassword',
                label: 'Confirm new password',
                autoComplete: 'new-password',
                describedBy: showMismatch ? 'password-mismatch' : undefined
              })}
              {showMismatch && (
                <span className="account-field-error" id="password-mismatch">
                  <AlertCircle aria-hidden="true" style={{ width: 13, height: 13 }} />
                  Passwords do not match.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="account-section-footer">
          <button type="submit" className="primary-button" disabled={!canSubmit}>
            {loading && <span className="account-spinner" aria-hidden="true" />}
            {loading ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>
    </section>
  );
};

export default PasswordSection;
